import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { Alert } from "@mui/material";
import * as React from "react";

import { useNavigation } from "@/providers";
import { AlertDialog, RobotoButton } from "@/shared/components";
import { EntityCustomFields } from "@/shared/components/CustomFields/EntityCustomFields";
import { RobotoPrincipalUnderlineLink } from "@/shared/components/principal";
import { FormattedDateTime } from "@/shared/datetime";
import { TargetEntityType } from "@/shared/domain/customFields";
import {
  useDeleteSession,
  useUpdateSession,
} from "@/shared/domain/sessions/hooks";
import { userFacingErrorMessage } from "@/shared/http";
import { formatNanoDuration, formatNanoTimestamp } from "@/shared/time";

import { AddSessionToCollection } from "./AddSessionToCollection";
import styles from "./SessionDetailsTab.module.css";
import { SessionMetadata } from "./SessionMetadata";
import { SessionTags } from "./SessionTags";
import { useSessionOutletContext } from "./useSessionOutletContext";

const PLACEHOLDER = "—";

export function SessionDetailsTab() {
  const { session } = useSessionOutletContext();
  const { goto } = useNavigation();
  const { mutateAsync: deleteSession, error: deleteError } = useDeleteSession();
  const { mutateAsync: updateSession } = useUpdateSession();
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [customFieldsError, setCustomFieldsError] =
    React.useState<Error | null>(null);

  return (
    <div className={styles.root}>
      <DeleteErrorAlert error={deleteError} />
      <CustomFieldsErrorAlert
        error={customFieldsError}
        onClose={() => setCustomFieldsError(null)}
      />

      <section className={styles.card}>
        <h3 className={styles.cardHeading}>Properties</h3>
        <dl className={styles.propertyList}>
          <div className={styles.propertyRow}>
            <dt className={styles.propertyLabel}>Session ID</dt>
            <dd className={styles.propertyValueMono}>{session.session_id}</dd>
          </div>

          <div className={styles.propertyRow}>
            <dt className={styles.propertyLabel}>Duration</dt>
            <dd className={styles.propertyValue}>
              {session.min_timestamp_ns !== null &&
              session.max_timestamp_ns !== null
                ? formatNanoDuration(
                    session.max_timestamp_ns - session.min_timestamp_ns,
                  )
                : PLACEHOLDER}
            </dd>
          </div>

          <div className={styles.propertyRow}>
            <dt className={styles.propertyLabel}>Started</dt>
            <dd className={styles.propertyValue}>
              {session.min_timestamp_ns !== null
                ? formatNanoTimestamp(session.min_timestamp_ns)
                : PLACEHOLDER}
            </dd>
          </div>

          <div className={styles.propertyRow}>
            <dt className={styles.propertyLabel}>Tags</dt>
            <dd className={styles.propertyValue}>
              <SessionTags session={session} />
            </dd>
          </div>

          <div className={styles.propertyRow}>
            <dt className={styles.propertyLabel}>Metadata</dt>
            <dd className={styles.propertyValue}>
              <SessionMetadata session={session} />
            </dd>
          </div>

          <div className={styles.propertyRow}>
            <dt className={styles.propertyLabel}>Created</dt>
            <dd className={styles.propertyValue}>
              {session.created ? (
                <>
                  <FormattedDateTime value={session.created} />
                  {" by "}
                  <RobotoPrincipalUnderlineLink
                    className={styles.inlinePrincipal}
                    principal={session.created_by}
                  />
                </>
              ) : (
                PLACEHOLDER
              )}
            </dd>
          </div>

          <div className={styles.propertyRow}>
            <dt className={styles.propertyLabel}>Updated</dt>
            <dd className={styles.propertyValue}>
              {session.modified ? (
                <>
                  <FormattedDateTime value={session.modified} />
                  {" by "}
                  <RobotoPrincipalUnderlineLink
                    className={styles.inlinePrincipal}
                    principal={session.modified_by}
                  />
                </>
              ) : (
                PLACEHOLDER
              )}
            </dd>
          </div>
        </dl>
      </section>

      <EntityCustomFields
        entityType={TargetEntityType.Session}
        orgId={session.org_id}
        values={session.custom_fields}
        commit={(changeset) =>
          updateSession({
            sessionId: session.session_id,
            updates: { custom_fields_changeset: changeset },
          })
        }
        onError={setCustomFieldsError}
        variant="card"
      />

      <div className={styles.operations}>
        <span className={styles.operationsLabel}>Operations</span>
        <div className={styles.operationsButtons}>
          <AddSessionToCollection sessionId={session.session_id} />
          <RobotoButton
            eventName="DeleteSessionClicked"
            eventProperties={{ sessionId: session.session_id }}
            variant="outlined"
            size="small"
            color="error"
            startIcon={<DeleteOutlinedIcon />}
            onClick={() => setDeleteDialogOpen(true)}
          >
            Delete Session
          </RobotoButton>
        </div>
      </div>

      <AlertDialog
        dialogOpen={deleteDialogOpen}
        handleClose={() => setDeleteDialogOpen(false)}
        dialogTitle="Delete Session"
        dialogText={`Are you sure you want to delete ${session.name ? `${session.name} (${session.session_id})` : session.session_id}?`}
        dialogAction={async () => {
          await deleteSession({ sessionId: session.session_id });
          goto.sessions();
        }}
      />
    </div>
  );
}

function DeleteErrorAlert({ error }: { error: Error | null }) {
  if (!error) {
    return null;
  }
  return (
    <Alert severity="error" className={styles.errorAlert}>
      {error.message}
    </Alert>
  );
}

function CustomFieldsErrorAlert({
  error,
  onClose,
}: {
  error: Error | null;
  onClose: () => void;
}) {
  if (!error) {
    return null;
  }
  return (
    <Alert severity="error" className={styles.errorAlert} onClose={onClose}>
      {userFacingErrorMessage(error, "Failed to update custom fields.")}
    </Alert>
  );
}
